(function () {
  'use strict';

  var root = document.documentElement;
  root.classList.remove('no-js');

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  var finePointer = window.matchMedia('(pointer: fine)');

  var PHRASE = [0, 0.055, 0.185, 0.245, 0.4, 0.475, 0.63];

  function phraseDelay(index, unit) {
    var step = unit || 1000;
    var loop = Math.floor(index / PHRASE.length);
    var offset = PHRASE[index % PHRASE.length] + loop * 0.72;
    return Math.round(offset * step);
  }

  var light = { x: 50, y: 0, pending: false };

  function commitLight() {
    light.pending = false;
    root.style.setProperty('--light-x', light.x.toFixed(2) + '%');
    root.style.setProperty('--light-y', light.y.toFixed(2) + '%');
  }

  function requestLight(x, y) {
    light.x = x;
    light.y = y;
    if (light.pending) return;
    light.pending = true;
    requestAnimationFrame(commitLight);
  }

  function onPointerMove(event) {
    // clientWidth/clientHeight excluent la barre de défilement, contrairement à
    // innerWidth/innerHeight. C'est la même zone que celle où le halo peint son
    // dégradé : sans ça il reste un décalage de la largeur de la barre.
    requestLight(
      (event.clientX / root.clientWidth) * 100,
      (event.clientY / root.clientHeight) * 100
    );
  }

  function onScrollLight() {
    var max = root.scrollHeight - root.clientHeight;
    var progress = max > 0 ? window.scrollY / max : 0;
    requestLight(50, progress * 100);
  }

  function startLight() {
    if (reduced.matches) {
      requestLight(12, 8);
      return;
    }
    if (finePointer.matches) {
      window.addEventListener('pointermove', onPointerMove, { passive: true });
      return;
    }
    window.addEventListener('scroll', onScrollLight, { passive: true });
    onScrollLight();
  }

  function stopLight() {
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('scroll', onScrollLight);
  }

  function restartLight() {
    stopLight();
    startLight();
  }

  var cursor = null;

  function startCursor() {
    if (reduced.matches || !finePointer.matches) return;
    if (root.dataset.customCursor !== 'true') return;
    if (cursor) return;

    cursor = document.createElement('div');
    cursor.className = 'cursor-dot';
    cursor.setAttribute('aria-hidden', 'true');
    cursor.appendChild(document.createElement('span')).className = 'cursor-dot__disc';
    if (supportsPopover) cursor.setAttribute('popover', 'manual');
    document.body.appendChild(cursor);
    promoteCursor();
    root.classList.add('has-custom-cursor');

    function onMove(event) {
      // Écrit sans passer par requestAnimationFrame, contrairement à --light-x/--light-y :
      // ces deux-là repeignent des dégradés sur toute la page et méritent d'être groupés,
      // alors qu'un transform sur une couche déjà promue ne coûte qu'une recomposition.
      // Passer par une frame ajoutait jusqu'à 16 ms de retard, soit une quinzaine de
      // pixels derrière le pointeur dès qu'on déplace la souris vite.
      cursor.style.transform = 'translate3d(' + event.clientX + 'px,' + event.clientY + 'px,0)';
      cursor.classList.add('is-active');

      var target = event.target instanceof Element ? event.target : null;
      var interactive = target ? target.closest('a, button, summary, label, select, [role="button"]') : null;
      var text = target ? target.closest('input:not([type="checkbox"]):not([type="radio"]):not([type="submit"]), textarea') : null;
      cursor.classList.toggle('is-over-link', Boolean(interactive) && !text);
      cursor.classList.toggle('is-over-text', Boolean(text));
    }

    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerdown', function () {
      cursor.classList.add('is-pressed');
    }, { passive: true });
    window.addEventListener('pointerup', function () {
      cursor.classList.remove('is-pressed');
    }, { passive: true });

    document.documentElement.addEventListener('mouseleave', function () {
      cursor.classList.remove('is-active');
    });
    document.documentElement.addEventListener('mouseenter', function () {
      cursor.classList.add('is-active');
    });
    window.addEventListener('blur', function () {
      cursor.classList.remove('is-active');
    });
  }

  function stopCursor() {
    root.classList.remove('has-custom-cursor');
    if (!cursor) return;
    cursor.remove();
    cursor = null;
  }

  // Un <dialog> ouvert par showModal() est promu dans le « top layer » : il se peint
  // au-dessus de tout le document, z-index compris. --z-cursor ne peut donc pas gagner.
  // On promeut le curseur dans cette même couche via l'API Popover : contrairement à un
  // second dialog modal, un popover ne piège pas le focus et n'inerte rien.
  var supportsPopover = typeof HTMLElement !== 'undefined' && 'showPopover' in HTMLElement.prototype;

  function isModal(node) {
    try {
      return node.matches(':modal');
    } catch (error) {
      return node.hasAttribute('open');
    }
  }

  function topModal() {
    var open = document.querySelectorAll('dialog[open]');
    for (var i = open.length - 1; i >= 0; i -= 1) {
      if (isModal(open[i])) return open[i];
    }
    return null;
  }

  function promoteCursor() {
    if (!cursor) return;

    if (supportsPopover) {
      // Dans le top layer, l'ordre de peinture suit l'ordre de promotion : il faut donc
      // re-promouvoir le curseur APRÈS l'ouverture du dialog pour repasser devant lui.
      try {
        if (cursor.matches(':popover-open')) cursor.hidePopover();
        cursor.showPopover();
        return;
      } catch (error) {
        supportsPopover = false;
      }
    }

    // Repli sans Popover : on déplace le curseur dans le dialog ouvert, qui est déjà
    // dans le top layer. Le curseur reste en position:fixed, donc il couvre le viewport.
    var host = topModal() || document.body;
    if (cursor.parentElement !== host) host.appendChild(cursor);
  }

  function hostCursor(node) {
    if (!cursor) return;
    if (node && !supportsPopover) {
      if (cursor.parentElement !== node) node.appendChild(cursor);
      return;
    }
    promoteCursor();
  }

  function watchTopLayer() {
    if (!('MutationObserver' in window)) return;

    // On n'écoute que l'attribut open, jamais childList : la vitrine réordonne ses
    // cartes à chaque filtre, et re-promouvoir le curseur à chaque nœud déplacé le
    // ferait clignoter pour rien. Le callback d'un MutationObserver s'exécute déjà
    // après le tick où showModal() a promu le dialog — pas besoin d'attendre une frame.
    var observer = new MutationObserver(function (records) {
      var touched = records.some(function (record) {
        return record.target.tagName === 'DIALOG';
      });
      if (touched) promoteCursor();
    });

    observer.observe(document.documentElement, {
      subtree: true,
      attributes: true,
      attributeFilter: ['open']
    });
  }

  function startReveals() {
    // On ignore ce qui est deja apparu : l'editeur de theme rejoue cette fonction a
    // chaque rechargement de section, et re-observer un element deja revele ne sert
    // qu'a le faire clignoter.
    var targets = document.querySelectorAll('.reveal:not(.is-revealed)');
    if (!targets.length) return;

    if (reduced.matches || !('IntersectionObserver' in window)) {
      targets.forEach(function (node) {
        node.classList.add('is-revealed');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var group = entry.target.parentElement
            ? Array.prototype.filter.call(entry.target.parentElement.children, function (child) {
                return child.classList.contains('reveal');
              })
            : [entry.target];
          var index = group.indexOf(entry.target);
          entry.target.style.setProperty('--reveal-delay', phraseDelay(index < 0 ? 0 : index, 720) + 'ms');
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.15 }
    );

    targets.forEach(function (node) {
      observer.observe(node);
    });
  }

  function onEnvironmentChange() {
    restartLight();
    if (reduced.matches || !finePointer.matches) {
      stopCursor();
    } else {
      startCursor();
    }
  }

  reduced.addEventListener('change', onEnvironmentChange);
  finePointer.addEventListener('change', onEnvironmentChange);

  function init() {
    startLight();
    startCursor();
    startReveals();
    watchTopLayer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // L'editeur de theme remplace le noeud d'une section a chaque modification. Les
  // nouveaux .reveal arrivent donc sans observateur : sans ce rappel ils restent a
  // opacity 0, et la section paraît vide pendant qu'on la regle.
  document.addEventListener('shopify:section:load', function () {
    startReveals();
    promoteCursor();
  });

  window.GIGI = {
    hostCursor: hostCursor,
    PHRASE: PHRASE,
    phraseDelay: phraseDelay,
    ease: {
      gigi: 'cubic-bezier(0.16, 0.84, 0.24, 1)',
      suspension: 'cubic-bezier(0.2, 1.12, 0.28, 1)',
      impulsion: 'cubic-bezier(0.7, 0, 0.84, 0)'
    },
    refreshReveals: startReveals
  };
})();
